import { useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { Icon } from "../../ui/Icon";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../../ui/Toast";
import { SearchSelect, type SSOption } from "../../ui/SearchSelect";
import { Drawer } from "../../Drawer";
import { DocChip } from "../../shipment/DocChip";
import { FilePreviewModal } from "../../shipment/FilePreviewModal";
import { useIsMobile } from "../../../lib/useIsMobile";
import { MCard } from "../../../components/mobile/primitives";
import "../../../pages/Items.css";

/**
 * İnsan Kaynakları — İşe Alım sekmesi.
 *
 * trpc.hr.recruitment.posting.list/get/create/update/remove +
 * trpc.hr.recruitment.candidate.list/create/update/setStage/remove/uploadCv.
 *
 * Üst: ilan tablosu (kod ILN-, başlık, pozisyon, açık pozisyon, aday sayısı, durum rozeti).
 * İlana tıkla → çekmece: ilan künyesi (düzenlenebilir) + adaylar (canlı aşama akışı, CV).
 * Not: create ucu `status` KABUL ETMEZ (her ilan "draft" başlar) → durum seçici yalnız düzenlemede.
 * Yazma aksiyonları yalnız `canManage` (manage:hr).
 */

const POSTING_STATUS_ORDER = ["draft", "open", "on_hold", "closed", "cancelled"] as const;
type PostingStatus = (typeof POSTING_STATUS_ORDER)[number];

/** İlan durumu = renk + ikon + metin (renk tek başına anlatmaz). */
const POSTING_STATUS_META: Record<PostingStatus, { cls: string; label: string; icon: string }> = {
  draft: { cls: "b-warn", label: "Taslak", icon: "edit" },
  open: { cls: "b-success", label: "Açık", icon: "play" },
  on_hold: { cls: "b-info", label: "Beklemede", icon: "clock" },
  closed: { cls: "b-primary", label: "Kapandı", icon: "checkCircle" },
  cancelled: { cls: "", label: "İptal", icon: "x" },
};

const STAGE_ORDER = ["applied", "screening", "interview", "offer", "hired", "rejected", "withdrawn"] as const;
type CandidateStage = (typeof STAGE_ORDER)[number];

/** Aday aşaması = renk + ikon + metin. */
const STAGE_META: Record<CandidateStage, { cls: string; label: string; icon: string }> = {
  applied: { cls: "b-info", label: "Başvurdu", icon: "inbox" },
  screening: { cls: "b-warn", label: "Ön Eleme", icon: "search" },
  interview: { cls: "b-primary", label: "Mülakat", icon: "users" },
  offer: { cls: "b-info", label: "Teklif", icon: "fileText" },
  hired: { cls: "b-success", label: "İşe Alındı", icon: "checkCircle" },
  rejected: { cls: "b-danger", label: "Reddedildi", icon: "x" },
  withdrawn: { cls: "", label: "Vazgeçti", icon: "minus" },
};

interface CandidateRow {
  id: string;
  postingId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  stage: string;
  cvUrl: string | null;
  notes: string | null;
  rating: number | null;
  appliedAt: string | Date;
  hiredEmployeeId: string | null;
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d));
}

/** Salt-okunur yıldız skoru (1-5). Renk + erişilebilir etiket birlikte. */
function RatingStars({ score, size = 13 }: { score: number | null; size?: number }) {
  if (score == null) return <span style={{ color: "var(--neutral-400)" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }} role="img" aria-label={`${score} / 5 puan`} title={`${score} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Icon key={n} name="star" size={size} style={{ color: n <= score ? "var(--warning)" : "var(--neutral-300)" }} />
      ))}
    </span>
  );
}

/** Tıklanabilir yıldız girişi (1-5). Aynı yıldıza tekrar tıkla → temizle. */
function StarInput({ value, onChange, disabled }: { value: number | null; onChange: (v: number | null) => void; disabled?: boolean }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((n) => {
        const on = value != null && n <= value;
        return (
          <button
            key={n}
            type="button"
            disabled={disabled}
            aria-label={`${n} puan`}
            aria-pressed={value === n}
            title={`${n} / 5`}
            onClick={() => onChange(value === n ? null : n)}
            style={{ background: "none", border: 0, padding: 2, cursor: disabled ? "default" : "pointer", lineHeight: 0, color: on ? "var(--warning)" : "var(--neutral-300)" }}
          >
            <Icon name="star" size={22} />
          </button>
        );
      })}
      <span style={{ marginLeft: 6, fontSize: 12, color: "var(--neutral-500)", fontVariantNumeric: "tabular-nums" }}>
        {value != null ? `${value} / 5` : "Puan yok"}
      </span>
    </div>
  );
}

// ── Aday ekle / düzenle çekmecesi (ilan çekmecesinin üstüne yığılır) ─────────

function CandidateFormDrawer({
  open,
  postingId,
  candidate,
  canManage,
  onClose,
  onSaved,
  onPreview,
  pushToast,
}: {
  open: boolean;
  postingId: string;
  candidate: CandidateRow | null; // null → yeni aday
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
  onPreview: (url: string) => void;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const isEdit = !!candidate;
  const qc = trpc.useUtils();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [cvUrl, setCvUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Açılışta alanları doldur (düzenleme) / sıfırla (yeni).
  useEffect(() => {
    if (!open) return;
    setErr(null);
    if (candidate) {
      setFirstName(candidate.firstName);
      setLastName(candidate.lastName);
      setEmail(candidate.email ?? "");
      setPhone(candidate.phone ?? "");
      setRating(candidate.rating ?? null);
      setNotes(candidate.notes ?? "");
      setCvUrl(candidate.cvUrl ?? null);
    } else {
      setFirstName(""); setLastName(""); setEmail(""); setPhone("");
      setRating(null); setNotes(""); setCvUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidate?.id]);

  const uploadMut = trpc.hr.recruitment.candidate.uploadCv.useMutation({
    onSuccess: (r) => {
      setCvUrl(r.url);
      if (isEdit) {
        qc.hr.recruitment.candidate.invalidate();
        pushToast({ tone: "success", message: "CV yüklendi." });
      }
    },
    onError: (e) => setErr(e.message),
  });

  const createMut = trpc.hr.recruitment.candidate.create.useMutation({
    onSuccess: () => {
      qc.hr.recruitment.candidate.invalidate();
      qc.hr.recruitment.posting.invalidate();
      pushToast({ tone: "success", message: "Aday eklendi." });
      onSaved();
      onClose();
    },
    onError: (e) => setErr(e.message),
  });

  const saveMut = trpc.hr.recruitment.candidate.update.useMutation({
    onSuccess: () => {
      qc.hr.recruitment.candidate.invalidate();
      pushToast({ tone: "success", message: "Aday güncellendi." });
      onSaved();
      onClose();
    },
    onError: (e) => setErr(e.message),
  });

  const busy = createMut.isPending || saveMut.isPending || uploadMut.isPending;
  const canSubmit = firstName.trim().length > 0 && lastName.trim().length > 0;

  function onPickCv(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    const reader = new FileReader();
    reader.onload = () => {
      if (isEdit && candidate) {
        uploadMut.mutate({ candidateId: candidate.id, filename: file.name, dataBase64: String(reader.result) });
      } else {
        uploadMut.mutate({ postingId, filename: file.name, dataBase64: String(reader.result) });
      }
    };
    reader.readAsDataURL(file);
  }

  function submit() {
    if (!canManage) return;
    if (!firstName.trim() || !lastName.trim()) { setErr("Ad ve soyad zorunlu."); return; }
    setErr(null);
    const shared = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      rating: rating ?? null,
      notes: notes.trim() || null,
    };
    if (isEdit && candidate) {
      saveMut.mutate({ id: candidate.id, ...shared });
    } else {
      createMut.mutate({ postingId, cvUrl: cvUrl ?? null, ...shared });
    }
  }

  const cvIsPdf = cvUrl ? cvUrl.toLowerCase().endsWith(".pdf") || cvUrl.startsWith("data:application/pdf") : false;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="md"
      dismissible={false}
      title={isEdit ? (candidate ? `${candidate.fullName} — Adayı Düzenle` : "Adayı Düzenle") : "Yeni Aday"}
      subtitle={isEdit ? "Aday bilgilerini güncelleyin" : "Bu ilana yeni aday ekleyin"}
      footer={
        <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
          {canManage ? (
            <button type="button" className="btn-md primary" onClick={submit} disabled={busy || !canSubmit}>
              {busy ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Ekle"}
            </button>
          ) : null}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
        </div>
      }
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cand-fn">Ad <span className="req">*</span></label>
            <input id="cand-fn" className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={120} disabled={!canManage} autoFocus={!isEdit} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="cand-ln">Soyad <span className="req">*</span></label>
            <input id="cand-ln" className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={120} disabled={!canManage} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="cand-email">E-posta</label>
            <input id="cand-email" className="form-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} disabled={!canManage} placeholder="ornek@firma.com" />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="cand-phone">Telefon</label>
            <input id="cand-phone" className="form-input" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} disabled={!canManage} />
          </div>
        </div>

        <div className="form-field">
          <span className="lbl">Değerlendirme Puanı</span>
          <StarInput value={rating} onChange={setRating} disabled={!canManage} />
        </div>

        <div className="form-field">
          <label className="lbl" htmlFor="cand-notes">Notlar</label>
          <textarea id="cand-notes" className="form-textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={4000} disabled={!canManage} />
        </div>

        <div className="form-field">
          <span className="lbl">Özgeçmiş (CV)</span>
          {cvUrl ? (
            <DocChip
              url={cvUrl}
              isPdf={cvIsPdf}
              label="CV"
              onOpen={onPreview}
              onRemove={canManage && !isEdit ? () => setCvUrl(null) : undefined}
              busy={uploadMut.isPending}
            />
          ) : (
            <span className="help">Henüz CV yüklenmedi.</span>
          )}
          {canManage ? (
            <div style={{ marginTop: 8 }}>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={onPickCv} />
              <button type="button" className="btn-md secondary" onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending}>
                <Icon name="upload" size={14} />{uploadMut.isPending ? "Yükleniyor…" : cvUrl ? "CV Değiştir" : "CV Yükle"}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}

// ── İlan künyesi + adaylar çekmecesi ────────────────────────────────────────

function PostingDrawer({
  open,
  postingId,
  canManage,
  isSuper,
  onClose,
  pushToast,
}: {
  open: boolean;
  postingId: string | null; // null → yeni ilan
  canManage: boolean;
  isSuper: boolean;
  onClose: () => void;
  pushToast: (t: Omit<ToastItem, "id">) => void;
}) {
  const isEdit = !!postingId;
  const qc = trpc.useUtils();

  const [title, setTitle] = useState("");
  const [positionId, setPositionId] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [requirements, setRequirements] = useState("");
  const [openingsStr, setOpeningsStr] = useState("1");
  const [status, setStatus] = useState<PostingStatus>("draft");
  const [err, setErr] = useState<string | null>(null);

  const [candForm, setCandForm] = useState<{ mode: "new" } | { mode: "edit"; candidate: CandidateRow } | null>(null);
  const [delCandidate, setDelCandidate] = useState<CandidateRow | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const getQ = trpc.hr.recruitment.posting.get.useQuery({ id: postingId ?? "" }, { enabled: open && isEdit, staleTime: 3_000 });
  const p = getQ.data ?? null;

  const candidatesQ = trpc.hr.recruitment.candidate.list.useQuery({ postingId: postingId ?? "" }, { enabled: open && isEdit, staleTime: 5_000 });
  const candidates = (candidatesQ.data ?? []) as CandidateRow[];

  const positionsQ = trpc.hr.position.list.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const positionOptions = useMemo<SSOption[]>(
    () => (positionsQ.data ?? []).map((r) => ({
      value: r.id,
      label: r.title,
      desc: r.orgUnitName ?? undefined,
      tail: r.code ?? undefined,
    })),
    [positionsQ.data],
  );

  // Açılışta / ilan değişince sıfırla; düzenlemede alanlar `p` gelince dolar.
  useEffect(() => {
    if (!open) return;
    setErr(null); setCandForm(null); setDelCandidate(null); setPreviewUrl(null);
    if (!isEdit) {
      setTitle(""); setPositionId(null); setDescription(""); setRequirements(""); setOpeningsStr("1"); setStatus("draft");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, postingId]);

  useEffect(() => {
    if (!p) return;
    setTitle(p.title);
    setPositionId(p.positionId ?? null);
    setDescription(p.description ?? "");
    setRequirements(p.requirements ?? "");
    setOpeningsStr(String(p.openings ?? 1));
    setStatus((p.status as PostingStatus) ?? "draft");
  }, [p]);

  const createMut = trpc.hr.recruitment.posting.create.useMutation({
    onSuccess: (r) => {
      qc.hr.recruitment.posting.invalidate();
      pushToast({ tone: "success", message: `İlan oluşturuldu (${r.code}).` });
      onClose();
    },
    onError: (e) => setErr(e.message),
  });
  const saveMut = trpc.hr.recruitment.posting.update.useMutation({
    onSuccess: () => {
      qc.hr.recruitment.posting.invalidate();
      getQ.refetch();
      pushToast({ tone: "success", message: "İlan güncellendi." });
    },
    onError: (e) => setErr(e.message),
  });
  const setStageMut = trpc.hr.recruitment.candidate.setStage.useMutation({
    onSuccess: () => { qc.hr.recruitment.candidate.invalidate(); },
    onError: (e) => pushToast({ tone: "danger", title: "Aşama değiştirilemedi", message: e.message }),
  });
  const removeCandMut = trpc.hr.recruitment.candidate.remove.useMutation({
    onSuccess: () => {
      qc.hr.recruitment.candidate.invalidate();
      qc.hr.recruitment.posting.invalidate();
      setDelCandidate(null);
      pushToast({ tone: "success", message: "Aday silindi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message }),
  });

  const busy = createMut.isPending || saveMut.isPending;
  const canSubmit = title.trim().length > 0;
  const loadingDetail = isEdit && getQ.isLoading;

  function submit() {
    if (!canManage) return;
    if (!title.trim()) { setErr("İlan başlığı zorunlu."); return; }
    setErr(null);
    const openings = Math.max(1, Math.min(9999, parseInt(openingsStr, 10) || 1));
    const shared = {
      title: title.trim(),
      positionId: positionId ?? null,
      description: description.trim() || null,
      requirements: requirements.trim() || null,
      openings,
    };
    if (isEdit && postingId) {
      saveMut.mutate({ id: postingId, status, ...shared });
    } else {
      createMut.mutate(shared);
    }
  }

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        size={isEdit ? "xl" : "lg"}
        dismissible={false}
        title={isEdit ? (p ? `${p.code} — İlanı Düzenle` : "İlanı Düzenle") : "Yeni İlan"}
        subtitle={isEdit ? "Künye ve adaylar" : "Kod otomatik üretilir (ILN-yıl-sıra) · ilan taslak olarak başlar"}
        footer={
          <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
            {canManage ? (
              <button type="button" className="btn-md primary" onClick={submit} disabled={busy || !canSubmit}>
                {busy ? "Kaydediliyor…" : isEdit ? "Kaydet" : "Oluştur"}
              </button>
            ) : null}
            <div style={{ flex: 1 }} />
            <button type="button" className="btn-md ghost" onClick={onClose}>{canManage ? "Vazgeç" : "Kapat"}</button>
          </div>
        }
      >
        {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

        {loadingDetail ? (
          <p className="td-empty" style={{ color: "var(--neutral-500)" }}>Yükleniyor…</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {isEdit && p ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span className={`badge-pill ${POSTING_STATUS_META[status]?.cls ?? ""}`}>
                  <Icon name={POSTING_STATUS_META[status]?.icon ?? "inbox"} size={11} />{POSTING_STATUS_META[status]?.label ?? status}
                </span>
                {isSuper && p.tesisName ? (
                  <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>Tesis: {p.tesisName}</span>
                ) : null}
                <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>Oluşturma: {fmtDate(p.createdAt)}</span>
              </div>
            ) : null}

            <div className="form-field">
              <label className="lbl" htmlFor="posting-title">İlan Başlığı <span className="req">*</span></label>
              <input id="posting-title" className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} disabled={!canManage} placeholder="Örn. Üretim Operatörü (Vardiyalı)" autoFocus={!isEdit} />
            </div>

            <div className="form-row">
              <div className="form-field">
                <span className="lbl">Pozisyon</span>
                <SearchSelect
                  value={positionId}
                  options={positionOptions}
                  onChange={setPositionId}
                  placeholder="Pozisyon seç…"
                  searchPlaceholder="Pozisyon ara…"
                  emptyText="Pozisyon yok"
                  icon="columns"
                  disabled={!canManage}
                />
                <span className="help">İlanı bir kadro pozisyonuna bağlayabilirsiniz (opsiyonel).</span>
              </div>
              <div className="form-field">
                <label className="lbl" htmlFor="posting-openings">Açık Pozisyon Sayısı</label>
                <input
                  id="posting-openings"
                  className="form-input"
                  type="number"
                  min={1}
                  max={9999}
                  value={openingsStr}
                  onChange={(e) => setOpeningsStr(e.target.value)}
                  onBlur={() => setOpeningsStr(String(Math.max(1, Math.min(9999, parseInt(openingsStr, 10) || 1))))}
                  disabled={!canManage}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                />
              </div>
            </div>

            {isEdit ? (
              <div className="form-field">
                <label className="lbl" htmlFor="posting-status">Durum</label>
                <select id="posting-status" className="form-select" value={status} onChange={(e) => setStatus(e.target.value as PostingStatus)} disabled={!canManage}>
                  {POSTING_STATUS_ORDER.map((s) => <option key={s} value={s}>{POSTING_STATUS_META[s].label}</option>)}
                </select>
              </div>
            ) : null}

            <div className="form-field">
              <label className="lbl" htmlFor="posting-desc">Açıklama</label>
              <textarea id="posting-desc" className="form-textarea" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000} disabled={!canManage} placeholder="Görev tanımı, çalışma koşulları…" />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="posting-req">Aranan Nitelikler</label>
              <textarea id="posting-req" className="form-textarea" rows={4} value={requirements} onChange={(e) => setRequirements(e.target.value)} maxLength={4000} disabled={!canManage} placeholder="Deneyim, eğitim, sertifika, yetkinlik…" />
            </div>

            {/* ── Adaylar ── */}
            {isEdit && p ? (
              <section style={{ display: "flex", flexDirection: "column", gap: 12, borderTop: "1px solid var(--neutral-200)", paddingTop: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="lbl" style={{ margin: 0 }}>Adaylar ({candidates.length})</span>
                  <div style={{ flex: 1 }} />
                  {canManage ? (
                    <button type="button" className="btn-md secondary" onClick={() => setCandForm({ mode: "new" })}>
                      <Icon name="plus" size={14} />Aday Ekle
                    </button>
                  ) : null}
                </div>

                {candidatesQ.isLoading ? (
                  <p className="td-empty" style={{ color: "var(--neutral-500)" }}>Yükleniyor…</p>
                ) : candidates.length === 0 ? (
                  <div style={{ padding: "14px 12px", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", background: "var(--neutral-50)", fontSize: 12.5, color: "var(--neutral-500)", textAlign: "center" }}>
                    Henüz aday yok{canManage ? " — “Aday Ekle” ile başvuranları kaydedin." : "."}
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {candidates.map((c) => {
                      const st = c.stage as CandidateStage;
                      const m = STAGE_META[st] ?? STAGE_META.applied;
                      const cvIsPdf = c.cvUrl ? c.cvUrl.toLowerCase().endsWith(".pdf") || c.cvUrl.startsWith("data:application/pdf") : false;
                      return (
                        <div key={c.id} style={{ display: "flex", flexDirection: "column", gap: 8, padding: 12, border: "1px solid var(--neutral-200)", borderRadius: "var(--radius-md)", background: "var(--white)" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                              <span style={{ fontWeight: 600, fontSize: 13.5, color: "var(--neutral-800)" }}>{c.fullName}</span>
                              <span style={{ fontSize: 12, color: "var(--neutral-500)" }}>
                                {c.email ?? "—"}{c.phone ? ` · ${c.phone}` : ""}
                              </span>
                            </div>
                            <span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span>
                          </div>

                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <RatingStars score={c.rating} />
                            <span style={{ fontSize: 11.5, color: "var(--neutral-400)", fontFamily: "var(--font-mono)" }}>Başvuru: {fmtDate(c.appliedAt)}</span>
                            <div style={{ flex: 1 }} />
                            {canManage ? (
                              <select
                                className="form-select"
                                value={st}
                                onChange={(e) => setStageMut.mutate({ id: c.id, stage: e.target.value as CandidateStage })}
                                disabled={setStageMut.isPending}
                                aria-label={`${c.fullName} aşamasını değiştir`}
                                style={{ maxWidth: 160, height: 34 }}
                              >
                                {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGE_META[s].label}</option>)}
                              </select>
                            ) : null}
                            {canManage ? (
                              <button type="button" className="iact" title="Düzenle" aria-label={`${c.fullName}: adayı düzenle`} onClick={() => setCandForm({ mode: "edit", candidate: c })}>
                                <Icon name="edit" size={14} />
                              </button>
                            ) : null}
                            {canManage ? (
                              <button type="button" className="iact danger" title="Sil" aria-label={`${c.fullName}: adayı sil`} onClick={() => setDelCandidate(c)}>
                                <Icon name="trash" size={14} />
                              </button>
                            ) : null}
                          </div>

                          {c.cvUrl ? (
                            <div>
                              <DocChip url={c.cvUrl} isPdf={cvIsPdf} label="CV" onOpen={setPreviewUrl} />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            ) : null}
          </div>
        )}
      </Drawer>

      {postingId ? (
        <CandidateFormDrawer
          open={!!candForm}
          postingId={postingId}
          candidate={candForm?.mode === "edit" ? candForm.candidate : null}
          canManage={canManage}
          onClose={() => setCandForm(null)}
          onSaved={() => { qc.hr.recruitment.candidate.invalidate(); }}
          onPreview={setPreviewUrl}
          pushToast={pushToast}
        />
      ) : null}

      <ConfirmDialog
        open={!!delCandidate}
        title="Adayı sil"
        message={delCandidate ? `"${delCandidate.fullName}" adayı kalıcı olarak silinecek. Yüklü CV varsa o da silinir. Bu işlem geri alınamaz.` : ""}
        confirmLabel="Sil"
        tone="danger"
        busy={removeCandMut.isPending}
        onCancel={() => setDelCandidate(null)}
        onConfirm={() => delCandidate && removeCandMut.mutate({ id: delCandidate.id })}
      />

      <FilePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </>
  );
}

// ── Sekme ───────────────────────────────────────────────────────────────────

export function RecruitmentTab({ canManage, isSuper }: { canManage: boolean; isSuper: boolean }) {
  const isMobile = useIsMobile();
  const [fStatus, setFStatus] = useState<PostingStatus | "">("");
  const [drawer, setDrawer] = useState<{ mode: "new" } | { mode: "edit"; id: string } | null>(null);
  const [delTarget, setDelTarget] = useState<{ id: string; code: string | null; title: string; candidateCount: number } | null>(null);

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) => setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  const qc = trpc.useUtils();

  const listQ = trpc.hr.recruitment.posting.list.useQuery(undefined, { staleTime: 5_000 });
  const allRows = listQ.data ?? [];
  const rows = useMemo(() => (fStatus ? allRows.filter((r) => r.status === fStatus) : allRows), [allRows, fStatus]);

  const removeMut = trpc.hr.recruitment.posting.remove.useMutation({
    onSuccess: (r) => {
      qc.hr.recruitment.posting.invalidate();
      setDelTarget(null);
      pushToast({ tone: "success", message: r.removedCandidates > 0 ? `İlan ve ${r.removedCandidates} aday silindi.` : "İlan silindi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message }),
  });

  // Aksiyon overlay'leri (çekmece + onay + toast) — masaüstü ve mobil return'ün ikisinde de en sonda.
  const drawers = (
    <>
      <PostingDrawer
        open={!!drawer}
        postingId={drawer?.mode === "edit" ? drawer.id : null}
        canManage={canManage}
        isSuper={isSuper}
        onClose={() => setDrawer(null)}
        pushToast={pushToast}
      />

      <ConfirmDialog
        open={!!delTarget}
        title="İlanı sil"
        message={
          delTarget
            ? delTarget.candidateCount > 0
              ? `"${delTarget.title}" (${delTarget.code}) ilanı ve buna bağlı ${delTarget.candidateCount} aday kalıcı olarak silinecek. Bu işlem geri alınamaz.`
              : `"${delTarget.title}" (${delTarget.code}) ilanı kalıcı olarak silinecek. Bu işlem geri alınamaz.`
            : ""
        }
        confirmLabel="Sil"
        tone="danger"
        busy={removeMut.isPending}
        onCancel={() => setDelTarget(null)}
        onConfirm={() => delTarget && removeMut.mutate({ id: delTarget.id })}
      />

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );

  if (isMobile) {
    const mb = (cls: string) => (cls === "b-warn" ? "b-warning" : cls || "b-neutral");
    return (
      <>
        <div className="m-stickytop">
          {canManage ? (
            <button type="button" className="btn-md primary" style={{ justifyContent: "center" }} onClick={() => setDrawer({ mode: "new" })}>
              <Icon name="plus" size={14} />İlan Ekle
            </button>
          ) : null}
          <div className="m-chips">
            {([["", "Tüm durumlar"], ...POSTING_STATUS_ORDER.map((s) => [s, POSTING_STATUS_META[s].label] as [string, string])] as Array<[string, string]>).map(([v, lb]) => (
              <button key={lb} type="button" className={`m-chip${fStatus === v ? " act" : ""}`} onClick={() => setFStatus(v as PostingStatus | "")}>{lb}</button>
            ))}
          </div>
        </div>

        {listQ.isError ? (
          <div className="m-empty">İlanlar yüklenemedi: {listQ.error.message}</div>
        ) : listQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="m-empty">{fStatus ? "Filtreye uygun ilan yok" : "İlan yok"}</div>
        ) : (
          rows.map((r) => {
            const st = r.status as PostingStatus;
            const m = POSTING_STATUS_META[st] ?? POSTING_STATUS_META.draft;
            return (
              <MCard pad key={r.id} onClick={() => setDrawer({ mode: "edit", id: r.id })} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.title}</span>
                    <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.code}</span>
                  </span>
                  <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  <div style={{ flex: 1 }}><div className="m-ovl">Pozisyon</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.positionTitle ?? "—"}</div></div>
                  <div style={{ flex: 1 }}><div className="m-ovl">Açık</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.openings}</div></div>
                  <div style={{ flex: 1 }}><div className="m-ovl">Aday</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.candidateCount}</div></div>
                </div>
                {canManage ? (
                  <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="m-iconbtn m-li-act danger" aria-label={`${r.code}: ilanı sil`} onClick={() => setDelTarget({ id: r.id, code: r.code, title: r.title, candidateCount: r.candidateCount })}>
                      <Icon name="trash" size={16} />
                    </button>
                  </div>
                ) : null}
              </MCard>
            );
          })
        )}

        {drawers}
      </>
    );
  }

  return (
    <div className="dt2-wrap">
      <div className="dt2-toolbar">
        <div className="dt2-filter sm">
          <select className="form-select" value={fStatus} onChange={(e) => setFStatus(e.target.value as PostingStatus | "")} aria-label="Durum filtresi">
            <option value="">Tüm durumlar</option>
            {POSTING_STATUS_ORDER.map((s) => <option key={s} value={s}>{POSTING_STATUS_META[s].label}</option>)}
          </select>
        </div>
        {fStatus ? (
          <button type="button" className="btn-md ghost" onClick={() => setFStatus("")}>
            <Icon name="x" size={14} />Filtreyi temizle
          </button>
        ) : null}
        <div className="grow" />
        {canManage ? (
          <button type="button" className="btn-md primary" onClick={() => setDrawer({ mode: "new" })}>
            <Icon name="plus" size={14} />İlan Ekle
          </button>
        ) : null}
      </div>

      <div className="dt2-scroll">
        {listQ.isError ? (
          <div className="form-banner-err" style={{ margin: 14 }}>İlanlar yüklenemedi: {listQ.error.message}</div>
        ) : listQ.isLoading ? (
          <p className="td-empty" style={{ padding: 24, textAlign: "center", color: "var(--neutral-500)" }}>Yükleniyor…</p>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <span className="ic" aria-hidden="true"><Icon name="inbox" size={30} /></span>
            <p className="ttl">İlan yok</p>
            <p className="sub">
              {fStatus
                ? "Filtreye uygun ilan bulunmuyor."
                : canManage
                  ? "\"İlan Ekle\" ile ilk iş ilanınızı oluşturun; adayları ilan içinden yönetirsiniz."
                  : "Görüntülenecek ilan bulunmuyor."}
            </p>
          </div>
        ) : (
          <table className="dt2">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Kod</th>
                <th>Başlık</th>
                <th style={{ width: 220 }}>Pozisyon</th>
                <th className="num" style={{ width: 90 }}>Açık</th>
                <th className="num" style={{ width: 90 }}>Aday</th>
                <th style={{ width: 140 }}>Durum</th>
                {canManage ? <th style={{ width: 60 }} aria-label="Eylemler" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = r.status as PostingStatus;
                const m = POSTING_STATUS_META[st] ?? POSTING_STATUS_META.draft;
                return (
                  <tr key={r.id} onClick={() => setDrawer({ mode: "edit", id: r.id })} style={{ cursor: "pointer" }} title="İlan detayını aç">
                    <td className="cell-id">{r.code}</td>
                    <td>{r.title}</td>
                    <td>{r.positionTitle ?? <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                    <td className="cell-num">{r.openings}</td>
                    <td className="cell-num">{r.candidateCount}</td>
                    <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                    {canManage ? (
                      <td onClick={(e) => e.stopPropagation()}>
                        <span className="row-acts">
                          <button
                            type="button"
                            className="iact danger"
                            title="Sil"
                            aria-label={`${r.code}: ilanı sil`}
                            onClick={() => setDelTarget({ id: r.id, code: r.code, title: r.title, candidateCount: r.candidateCount })}
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        </span>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="dt2-foot">
        <span>{rows.length} ilan</span>
      </div>

      {drawers}
    </div>
  );
}
